import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalLoading } from 'components/ModalLoading';
import { TableTopPanel } from 'components/TableTopPanel';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { RequestStatus } from 'features/request/requestSlice';
import type { ContractResultsViewData } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { Loader, Button, IconInfo } from 'uikit';

import type { ColDef, GetRowIdParams, RowClassParams } from 'ag-grid-community';
import type React from 'react';
import { useMemo, useRef } from 'react';

import type { ParsedContractData } from '../utils';
import { getLabelContract } from '../utils';

import { useAccumulationSheetColumns } from './hooks/useAccumulationSheetColumns';
import { useUerExportContractResults } from './hooks/useUerExportContractResults';

import Style from './ContractAccumulationSheetGrid.module.css';

interface IContractAccumulationSheetGrid {
    contract: ParsedContractData;
    data?: ContractResultsViewData;
    hasWorkOrders: boolean;
    isLoading: boolean;
    hasWorkOrdersInProgressOrCompleted: boolean;
    costRounding?: number;
}

const getRowId = (params: GetRowIdParams): string => params.data.name;

const getRowClass = (params: RowClassParams) => [
    ...(params.data?.isHeadLine ? ['is-head-line'] : ['is-ordinary-line']),
    ...(params.data?.isEditable ? ['is-editable'] : []),
];

const style = { gridTemplateColumns: '1fr 50px' };

export const ContractAccumulationSheetGrid: React.FC<IContractAccumulationSheetGrid> = (props) => {
    const { isLoading, data, contract, hasWorkOrders, hasWorkOrdersInProgressOrCompleted, costRounding = 0 } = props;

    const { contract: contractRules } = useAccess();

    const { columns, isColumnVisible, handleTableSettingsConfirm, optionalColumns } = useAccumulationSheetColumns({
        contractTitle: getLabelContract(contract),
        isContractPlanOnly: !hasWorkOrders,
        constructionTypes: contract?.constructionTypes,
        hasNoPlanByConstructionType: contract.hasNoPlanByConstructionType,
        data,
        costRounding,
    });

    const priceTableApiRef = useRef(null);
    const tableApi = useMemo(
        () => (priceTableApiRef && 'current' in priceTableApiRef ? priceTableApiRef.current : undefined),
        [priceTableApiRef],
    );

    const { exportActKC3AsExcelAbort, exportActKC3AsExcelStatus, exportActKC3AsExcelMakeRequest } =
        useUerExportContractResults(contract?.id);

    return (
        <div className={`ag-theme-alpine ${Style.uerContractAccumulationSheet} agCustomHeaderColors`}>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <TableTopPanel
                        style={style}
                        optionalColumns={optionalColumns}
                        handleTableSettingsConfirm={handleTableSettingsConfirm}
                        isColumnVisible={isColumnVisible}
                        showPinColumn
                        showCheckedOnly
                        tableApi={tableApi}
                        leftSide={
                            <div className={Style.leftSideWrapper}>
                                <TooltipWrapper
                                    direction="rightCenter"
                                    message="Не создан Наряд-заказ"
                                    isHidden={hasWorkOrdersInProgressOrCompleted}
                                >
                                    <Button
                                        label="Выгрузить Накопительную ведомость"
                                        view="secondary"
                                        size="xs"
                                        disabled={!hasWorkOrdersInProgressOrCompleted || !contractRules.canExportExcel}
                                        onClick={exportActKC3AsExcelMakeRequest}
                                    />
                                </TooltipWrapper>
                                <div className={Style.leftSideInfo}>
                                    <IconInfo size="s" />
                                    <div>
                                        Прочие затраты в статусе «Новая запись» и «Условие Договора» с типом связи
                                        «Новая связь» не участвуют в расчете Плана по Договору
                                    </div>
                                </div>
                            </div>
                        }
                    />
                    <AgGridTable
                        rowData={data?.contractResults}
                        columnDefs={columns as ColDef[]}
                        overlayNoRowsTemplate="Нет данных"
                        getRowId={getRowId}
                        getRowClass={getRowClass}
                        ref={priceTableApiRef}
                        animateRows
                    />
                </>
            )}
            {exportActKC3AsExcelStatus === RequestStatus.FETCHING && (
                <ModalLoading title="Выгрузка Накопительной ведомости" onClose={exportActKC3AsExcelAbort} />
            )}
        </div>
    );
};

ContractAccumulationSheetGrid.displayName = 'ContractAccumulationSheetGrid';
